import { z } from 'zod';
import { objectiveStatusSchema, protocolLocalIdSchema } from './protocol.js';

/**
 * L'ONGLET MATIÈRE (refonte modulaire §12.4) - the extracts of one experience,
 * grouped by objective, across every session it has ever played.
 *
 * ## Why a file of its own beside `session-read.ts`
 *
 * `session-read.ts` is somebody opening ONE session and walking it: its shapes
 * all hang off a session id. This one is the first read of §12 that does not -
 * « la première vue de la matière première extraite qui ne passe pas par une
 * session à la fois ». It hangs off an EXPERIENCE, it answers by objective
 * rather than by session, and a session is a column of its answer. Putting it
 * in the reader's file would have made the reader's own sentence false.
 *
 * Nothing here redraws a stored shape either: an extract's text and its
 * `invalidated` flag are `snapshotExtractSchema`'s (`turn.ts`), a status is
 * `objectiveStatusSchema`'s. What is ADDED is the provenance - which session,
 * which turn - because that is exactly what a grouping loses, and it is what
 * sends a reader back to the conversation that produced a line.
 *
 * ## An invalidated extract is here, marked, never missing
 *
 * §8.4: « ses extraits restent dans le journal de la session marqués invalidés,
 * sa matière vivante repart de zéro ». So this contract carries the flag and
 * never filters on it. A corpus that silently dropped what stopped counting
 * would be a corpus nobody could audit - and the same text can legitimately
 * appear twice, invalidated on the turn that lost it and live on the turn the
 * person said it again.
 */

/* ----------------------------------------------------------------- the step */

/**
 * The step an objective belongs to, read on the FROZEN protocol (§5.2).
 *
 * Nullable on the objective below, because a grouping spans sessions that froze
 * different versions: an objective removed by a later version still has
 * extracts in the sessions that played it, and those extracts must not vanish
 * with the step that used to carry them.
 */
export const matterStepSchema = z
  .object({
    id: protocolLocalIdSchema,
    /** 1-based rank in the frozen protocol - the number the screens print. */
    index: z.number().int().positive(),
    name: z.string().min(1),
  })
  .strict();
export type MatterStep = z.infer<typeof matterStepSchema>;

/* -------------------------------------------------------------- the extract */

/** The session an extract came from: what a line is dated and addressed by. */
export const matterExtractSessionSchema = z
  .object({
    id: z.string().min(1),
    startedAt: z.string().datetime(),
  })
  .strict();
export type MatterExtractSession = z.infer<typeof matterExtractSessionSchema>;

/**
 * The turn an extract came from - the FIRST turn whose snapshot held it.
 *
 * The index and not only the id, because the deep link of §12.3 is written on
 * the index (`/admin/sessions/<id>/protocole?tour=<index>`): the conversation
 * scrolls to a rank, and an id alone would make the screen resolve one before
 * it could move. Both travel, so a reader can address the turn either way.
 */
export const matterExtractTurnSchema = z
  .object({
    id: z.string().min(1),
    /** 0-based, across the whole session and not per step (§8.7). */
    index: z.number().int().nonnegative(),
  })
  .strict();
export type MatterExtractTurn = z.infer<typeof matterExtractTurnSchema>;

/**
 * ONE extract, and everything needed to walk back to where it was said.
 *
 * `objectiveStatus` is the status the objective ENDED at IN THAT SESSION, and
 * not a fact about the objective: the same objective is `réussi` in one session
 * and `raté` in the next, which is the whole reason §12.4 makes the status a
 * FILTER. It rides on the extract rather than on the objective because that is
 * where a reader reads it - under the line, with the session that produced it.
 */
export const matterExtractSchema = z
  .object({
    /** The scribe's own spelling, blanks collapsed (`literalExtracts`). */
    text: z.string(),
    /** §8.4 - true says « ceci a cessé de compter », never « ceci est caché ». */
    invalidated: z.boolean(),
    session: matterExtractSessionSchema,
    turn: matterExtractTurnSchema,
    objectiveStatus: objectiveStatusSchema,
    /**
     * True when that status is a réussi the END of its step validated at the
     * passable bar (§7.3, issue #112) - the snapshot's `passableOnly`, carried
     * as it is: ABSENT otherwise. The status filter reads `objectiveStatus`
     * alone, and has to: a passable réussi IS a réussi.
     */
    objectivePassableOnly: z.boolean().optional(),
  })
  .strict();
export type MatterExtract = z.infer<typeof matterExtractSchema>;

/* ------------------------------------------------------------ the objective */

/**
 * One objective, with every extract every session attributed to it.
 *
 * Grouped on the objective's LOCAL id (§7.1), which is a uuid struck once by
 * the editor and kept across renames, reorders and versions - so two sessions
 * that played two versions of one protocol land on one line, and two different
 * protocols cannot collide into one.
 *
 * `name` and `mandatory` are read on the frozen protocol of the MOST RECENT
 * session that still carries the objective. When no session's frozen protocol
 * carries it any more - removed by a later version, or a version that no longer
 * reads - `name` falls back to the id and `step` is null: that null is what
 * says the whole line is a fallback, and `mandatory` is false only because a
 * boolean has to say something.
 *
 * An objective with no extract at all is ABSENT: §12.4 lists « les extraits par
 * objectif », and a page of empty headings is not a corpus.
 */
export const matterObjectiveSchema = z
  .object({
    objectiveId: protocolLocalIdSchema,
    name: z.string().min(1),
    mandatory: z.boolean(),
    step: matterStepSchema.nullable(),
    /** Newest session first, and inside a session in the order it attributed them. */
    extracts: z.array(matterExtractSchema),
  })
  .strict();
export type MatterObjective = z.infer<typeof matterObjectiveSchema>;

/* --------------------------------------------------------------- the answer */

/**
 * One experience version that has actually been PLAYED - what the version
 * filter offers.
 *
 * `number` is null when the version no longer reads: the sessions that ran on
 * it are still there and still filterable, so dropping the entry would hide
 * them behind a control that cannot name them.
 */
export const matterVersionSchema = z
  .object({
    id: z.string().min(1),
    number: z.number().int().positive().nullable(),
  })
  .strict();
export type MatterVersion = z.infer<typeof matterVersionSchema>;

/**
 * `GET /api/experiences/{id}/matter` - the whole of §12.4 for one experience.
 *
 * `versions` is computed BEFORE the version filter is applied, deliberately: it
 * is the list the control OFFERS, and a control that narrowed itself to the
 * value it already holds would trap an operator on the first version they
 * picked. Highest number first, unreadable ones last.
 */
export const experienceMatterSchema = z
  .object({
    versions: z.array(matterVersionSchema),
    /** In the order of the frozen protocol: step rank, then rank inside the step. */
    objectives: z.array(matterObjectiveSchema),
  })
  .strict();
export type ExperienceMatter = z.infer<typeof experienceMatterSchema>;

/**
 * The two filters of §12.4, both optional and both narrowing.
 *
 * `status` bears on the objective IN a session, so it selects (session,
 * objective) couples rather than rows of a table: a session where the objective
 * ended `raté` contributes nothing to a `succeeded` reading, even though the
 * objective is `réussi` elsewhere.
 *
 * `experienceVersionId` is a VERSION id and not a tag, at the régime of the
 * freeze (§5.2): what a session played is a version, and « la version jouée »
 * is what §12.1 already prints on every line.
 */
export const matterFiltersSchema = z
  .object({
    status: objectiveStatusSchema.optional(),
    experienceVersionId: z.string().min(1).optional(),
  })
  .strict();
export type MatterFilters = z.infer<typeof matterFiltersSchema>;
